import { templateRoute } from './routeTemplate';
import type {
  CollectorOptions,
  CompletedCall,
  ResourceTimingSlice,
  SendReason,
  TimingRecord,
} from './types';

/**
 * Records held before a flush. Also the cap on the buffer: past this the oldest
 * go, because a browser that cannot reach the collector must not accumulate an
 * ever-growing backlog of measurements nobody will read.
 */
export const BATCH_SIZE = 20;

/** How often a partial batch is flushed anyway. */
export const FLUSH_INTERVAL_MS = 30_000;

/**
 * How many times one batch is sent before it is abandoned.
 *
 * Two, deliberately. This telemetry travels over the same connection it is
 * measuring, so retrying hard would make a bad connection worse to report that
 * the connection is bad.
 */
const MAX_SEND_ATTEMPTS = 2;

/** A running collector. */
export interface TimingCollector {
  /** Offer a finished call. Sampling decides whether it is kept. */
  record: (call: CompletedCall) => void;
  /** Send whatever is buffered now. */
  flush: () => Promise<void>;
  /** Flush and stop the interval. */
  stop: () => void;
  /**
   * Buffered records, for tests and for support to read in the console.
   *
   * Read-only, and deliberately without the browser's connection/download
   * split: looking up a resource entry consumes it, so enriching here would
   * mean that reading the buffer changed what the next flush reports.
   */
  pending: () => TimingRecord[];
}

/**
 * Create a collector that batches client timings and hands them to `send`.
 *
 * The collector owns sampling, batching and flush scheduling. It deliberately
 * knows nothing about authentication or transport - the host app supplies
 * `send`, because Connect authenticates with a bearer token and console with a
 * cookie, and neither belongs in here.
 */
export function createTimingCollector(options: CollectorOptions): TimingCollector {
  const random = options.random ?? Math.random;
  // Calls are held un-built until flush. The browser's resource entry for a
  // request is not guaranteed to exist when the response callback runs, so
  // building the record immediately would drop the connection/download split
  // for an arbitrary share of calls. Batching already delays us by up to
  // FLUSH_INTERVAL_MS, by which point the entry has certainly landed.
  let buffer: Array<{ call: CompletedCall; reason: SendReason }> = [];
  let flushing = false;
  let timer: ReturnType<typeof setInterval> | undefined;

  function shouldKeep(totalMs: number): SendReason | undefined {
    // The sample is drawn first, and independently of how slow the call was.
    //
    // Checking slow first would make 'sample' a uniform slice of only the
    // sub-threshold calls - a truncated population - and every percentile
    // computed from it would read low. Drawing first means 'sample' is a
    // uniform slice of ALL calls, which is what makes the aggregate view
    // trustworthy.
    //
    // Multiply rather than divide so a 0 percent sample is exactly "never"
    // rather than a floating-point near-miss.
    if (random() * 100 < options.samplePercent) {
      return 'sample';
    }
    // Not sampled, but still worth having: every slow call is reported, which
    // is what the per-user view needs.
    if (totalMs >= options.slowCallMs) {
      return 'slow';
    }
    return undefined;
  }

  function record(call: CompletedCall): void {
    // Checked here as well as at flush so a disabled collector does not sit
    // quietly filling a buffer that will be thrown away.
    if (!options.isEnabled()) {
      return;
    }

    const reason = shouldKeep(call.totalMs);
    if (reason === undefined) {
      return;
    }

    buffer.push({ call, reason });

    // Drop oldest rather than newest: the most recent calls are the ones a user
    // is complaining about right now.
    if (buffer.length > BATCH_SIZE) {
      buffer = buffer.slice(-BATCH_SIZE);
    }

    if (buffer.length >= BATCH_SIZE) {
      void flush();
    }
  }

  async function flush(): Promise<void> {
    // A flush already in flight owns the current buffer. Starting a second
    // would double-send it, and on a slow connection flushes overlap easily.
    if (flushing || buffer.length === 0 || !options.isEnabled()) {
      return;
    }

    flushing = true;
    const batch = buffer.map(build);
    buffer = [];

    try {
      for (let attempt = 1; attempt <= MAX_SEND_ATTEMPTS; attempt += 1) {
        try {
          await options.send(batch);
          return;
        } catch {
          // Last attempt: the batch is dropped. Losing a few measurements is
          // the correct trade against holding the user's connection open.
          if (attempt === MAX_SEND_ATTEMPTS) {
            return;
          }
        }
      }
    } finally {
      flushing = false;
    }
  }

  function stop(): void {
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
    void flush();
  }

  timer = setInterval(() => {
    void flush();
  }, FLUSH_INTERVAL_MS);

  return {
    record,
    flush,
    stop,
    // Built without the resource-timing lookup on purpose - see the interface.
    pending: () => buffer.map(({ call, reason }) => toRecord(call, reason)),
  };

  /** Build the wire record, enriching it with the browser's own measurements. */
  function build({ call, reason }: { call: CompletedCall; reason: SendReason }): TimingRecord {
    return toRecord(call, reason, options.resourceTimings?.(call.url));
  }
}

/**
 * Build the wire record.
 *
 * Optional timings are left undefined rather than zeroed: legacy sends no
 * Server-Timing header, and a zero there would read as "the server took no
 * time" instead of "we could not tell".
 */
function toRecord(
  call: CompletedCall,
  reason: SendReason,
  timings?: ResourceTimingSlice,
): TimingRecord {
  const record: TimingRecord = {
    route: templateRoute(call.url),
    path: stripQuery(call.url),
    method: call.method.toUpperCase(),
    backend: call.backend,
    reason,
    totalMs: round(call.totalMs),
  };

  if (call.serverMs !== undefined && call.serverMs !== null) {
    record.serverMs = round(call.serverMs);
  }
  if (call.traceId) {
    record.traceId = call.traceId;
  }

  // Every one of these is optional on the wire. Cross-origin they are zeroed
  // unless the server sent Timing-Allow-Origin, and a request that never
  // reached the network has no entry at all.
  if (timings) {
    assignIfPositive(record, 'ttfbMs', timings.ttfbMs);
    assignIfPositive(record, 'downloadMs', timings.downloadMs);
    assignIfPositive(record, 'connectMs', timings.connectMs);
    assignIfPositive(record, 'dnsMs', timings.dnsMs);
    if (timings.transferSize !== undefined && timings.transferSize > 0) {
      record.transferSize = timings.transferSize;
    }
    if (timings.encodedBodySize !== undefined && timings.encodedBodySize > 0) {
      record.encodedBodySize = timings.encodedBodySize;
    }
  }

  return record;
}

/**
 * Set a duration only when it carries information.
 *
 * Resource Timing reports 0 both for "this phase took no measurable time" and
 * for "you are not allowed to see this", and cross-origin without
 * Timing-Allow-Origin gives the latter. Sending 0 would put a fake floor under
 * every percentile, so an unusable value is left off instead.
 */
function assignIfPositive(
  record: TimingRecord,
  field: 'ttfbMs' | 'downloadMs' | 'connectMs' | 'dnsMs',
  value: number | undefined,
): void {
  if (value !== undefined && value > 0) {
    record[field] = round(value);
  }
}

/**
 * Keep the path, drop the query string.
 *
 * A query string can hold free text a user typed into a search box, which is
 * unbounded in a way a path id is not.
 */
function stripQuery(url: string): string {
  const path = url.split('?')[0].split('#')[0];
  const schemeEnd = path.indexOf('://');
  if (schemeEnd === -1) {
    return path;
  }
  const afterHost = path.indexOf('/', schemeEnd + 3);
  return afterHost === -1 ? '/' : path.slice(afterHost);
}

/** One decimal place is plenty; the extra digits are noise in a histogram. */
function round(value: number): number {
  return Math.round(value * 10) / 10;
}
